import React from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import KeyIcon from '@mui/icons-material/Key'
import CheckIcon from '@mui/icons-material/Check'

import { useQuery, useCommand } from '~/client/hooks/cqrpc'
import useStateObject from '~/client/hooks/useStateObject'
import ErrorMessage from '~/client/components/ErrorMessage'
import Timestamp from '~/client/components/Timestamp.js'
import CopyButton from '~/client/components/CopyButton.js'
import AppForm from '../AppForm.js'

export default function Page() {
  let { host } = useParams()
  const query = useQuery('apps.getByHost', { host })
  const app = query.result
  const [justCreated, setJustCreated] = React.useState()
  const generateNewClientSecretCmd = useCommand('apps.generateNewClientSecret', {
    onSuccess(appClientSecret){
      console.log('CREATED', appClientSecret)
      setJustCreated(appClientSecret)
    }
  })
  if (query.loading) return <div>loading…</div>
  if (query.error) return <ErrorMessage error={query.error}/>
  return <Container p={2}>
    <Paper sx={{margin: 'auto', mt: 2, p: 2}}>
      <Typography variant="h3">{app.name}</Typography>
      <Box my={4}>
        <Stack sx={{ position: 'relative' }}>
          <Typography variant="h5">Client ID</Typography>
          <Typography variant="h6">{app?.id}</Typography>
        </Stack>
      </Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        my={4}
      >
        <Typography variant="h6">0 Users</Typography>
        <Button
          variant="outlined"
          color="error"
          size="small"
        >
          Revoke all user tokens
        </Button>
      </Stack>
      <Box my={4}>
        <Stack sx={{position: 'relative', mb: 2}}>
          <Button
            variant="outlined"
            color="info"
            size="small"
            sx={{position: 'absolute', top: 0, right: 0}}
            onClick={() => {
              generateNewClientSecretCmd.call({appId: app.id})
            }}
            disabled={generateNewClientSecretCmd.pending}
          >
            Generate a new client secret
          </Button>
          <ErrorMessage error={generateNewClientSecretCmd.error}/>
          <Typography variant="h5">Client Secrets</Typography>
        </Stack>
        <ClientSecrets {...{appId: app.id, justCreated}}/>
      </Box>
      <EditAppForm
        app={app}
        reload={query.reload}
      />
    </Paper>
  </Container>
}

function EditAppForm({ app, reload }) {
  const [changes, onChange] = useStateObject({})
  const cmd = useCommand('apps.update', {
    onSuccess(){
      onChange(undefined)
      reload()
    }
  })
  return <AppForm {...{
    submitText: ['Update App', 'Updating App…'],
    onSubmit(){ cmd.call({ ...changes, id: app.id }) },
    error: cmd.error,
    submittable: JSON.stringify(changes) !== '{}',
    submitting: cmd.pending,
    value: {
      ...app,
      ...changes,
    },
    onChange(patch, replace){
      if (replace) return onChange(patch, replace)
      const combined = {...changes, ...patch}
      for (const prop in combined)
        if (combined[prop] === app[prop])
          delete combined[prop]
      onChange(combined, true)
    },
  }}/>
}




function ClientSecrets({ appId, justCreated }){
  const clientSecretsQuery = useQuery('apps.getClientSecrets', { appId })
  React.useEffect(
    () => { if (justCreated) clientSecretsQuery.reload() },
    [justCreated]
  )
  const clientSecrets = clientSecretsQuery.result
  return <div>
    <ErrorMessage error={clientSecretsQuery.error}/>
    {(!clientSecrets || clientSecrets.length === 0)
      ? <Typography variant="body2" my={2}>
        You do not have any client secrets yet.
      </Typography>
      : <Stack spacing={2}>
        {clientSecrets.map(clientSecret =>
          <ClientSecret {...{ key: clientSecret.id, clientSecret, clientSecretsQuery, justCreated }} />
        )}
      </Stack>
    }
  </div>
}

function ClientSecret({ clientSecret, clientSecretsQuery, justCreated }){
  console.log({ clientSecret, justCreated })
  const wasJustCreated = justCreated && justCreated.id === clientSecret.id
  const deleteClientSecretCmd = useCommand('apps.deleteClientSecret', {
    onSuccess(){
      clientSecretsQuery.reload()
    },
    onFailure(error){
      console.error(error)
      toast.error(`Failed to delete client secret. Please try again later.`)
    }
  })
  // return <InspectObject object={clientSecret}/>
  return <Stack
    direction="row"
    sx={{
      border: 1,
      // borderWidth: '1px',
      // borderColor: 'primary.main',
      // borderStyle: 'solid',
      borderRadius: 1,
      borderColor: 'rgba(255, 255, 255, 0.25)', //TODO same as TextInput
      p: 2,
      backgroundColor: wasJustCreated ? 'success.dark' : 'transparent',
    }}
    spacing={2}
  >
    <Stack alignItems="center">
      <KeyIcon sx={{ fontSize: 80, opacity: 0.25 }}/>
      <Typography variant="body1">Client Secret</Typography>
    </Stack>
    <Stack sx={{flexGrow: 1}}>
      {wasJustCreated
        ? <Stack direction="row" alignItems="center">
          <CheckIcon sx={{mr: 1}}/>
          <Typography variant="h6" sx={{
            display: 'inline',
            overflow: 'hidden',
            textOverflow: 'clip'
          }}>{justCreated.secret}</Typography>
          <CopyButton variant="icon" value={justCreated.secret}/>
        </Stack>
        : <Typography variant="body1">{clientSecret.secret}</Typography>
      }
      <Typography variant="body1">
        <span> Added </span>
        <Timestamp at={clientSecret.createdAt}/>
        <span> by </span>
        <span>{clientSecret.creator?.displayName}</span>
      </Typography>
      <Typography>
        {clientSecret.lastUsed
          ? <span>last used <Timestamp at={clientSecret.lastUsed}/></span>
          : <span>never used</span>
        }
      </Typography>
      {clientSecretsQuery.result.length === 1
        ? <Typography variant="body2">
          You cannot delete the only client secret. Generate a new client secret first.
        </Typography>
        : null
      }
    </Stack>
    <Stack direction="row" alignItems="center">
      <Button
        variant="outlined"
        onClick={() => {
          const confirmed = confirm('Are you sure you want to delete this app client secret?')
          if (confirmed) deleteClientSecretCmd.call({ id: clientSecret.id })
        }}
        disabled={deleteClientSecretCmd.pending}
      >Delete</Button>
    </Stack>
  </Stack>
}
